/**
 * Serialization validation and safety utilities
 * Prevents non-serializable objects from entering React state
 */

/**
 * Tests if a value can be safely serialized
 * @param {any} value - Value to test
 * @returns {boolean} True if serializable
 */
export function isSerializable(value) {
  if (value === null || value === undefined) {
    return true
  }

  const type = typeof value
  
  // Primitives are always serializable
  if (type === 'string' || type === 'number' || type === 'boolean') {
    return true
  }

  // Functions are never serializable
  if (type === 'function') {
    return false
  }

  // Check for known non-serializable types
  if (value instanceof Error ||
      value instanceof Request ||
      value instanceof Response ||
      value instanceof Event ||
      value instanceof File ||
      value instanceof Blob) {
    return false
  }

  // Check constructor name for web APIs
  const constructorName = value?.constructor?.name
  if (constructorName && (
    constructorName.includes('Request') ||
    constructorName.includes('Response') ||
    constructorName.includes('Event') ||
    constructorName.includes('HTML') ||
    constructorName === 'Proxy'
  )) {
    return false
  }

  // For arrays, check all elements
  if (Array.isArray(value)) {
    return value.every(item => isSerializable(item))
  }

  // For objects, check all values
  if (type === 'object') {
    // Try to detect if it's a plain object
    if (Object.getPrototypeOf(value) !== Object.prototype && 
        Object.getPrototypeOf(value) !== null) {
      // Not a plain object, might not be serializable
      return false
    }

    try {
      // Check all values in the object
      return Object.values(value).every(v => isSerializable(v))
    } catch (e) {
      return false
    }
  }

  return false
}

/**
 * Ensures a value is serializable, returns safe version or throws
 * @param {any} value - Value to make safe
 * @param {string} context - Context for error message
 * @returns {any} Safe serializable value
 */
export function ensureSerializable(value, context = 'value') {
  if (isSerializable(value)) {
    return value
  }

  console.error(`[Serialization] Non-serializable ${context}:`, typeof value, value?.constructor?.name)
  
  // Try to extract serializable parts
  if (value && typeof value === 'object') {
    const safe = {}
    for (const [key, val] of Object.entries(value)) {
      if (isSerializable(val)) {
        safe[key] = val
      }
    }
    return safe
  }

  // Fallback to null
  return null
}

/**
 * Test if value can be cloned with structuredClone
 * @param {any} value - Value to test
 * @returns {boolean} True if cloneable
 */
export function isCloneable(value) {
  try {
    structuredClone(value)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Safe setState wrapper that validates serializability
 * @param {Function} setState - React setState function
 * @param {any} value - Value to set
 * @param {string} stateName - Name of state for debugging
 */
export function safeSetState(setState, value, stateName = 'state') {
  try {
    // Validate serializability
    if (!isSerializable(value)) {
      console.error(`[SafeSetState] Non-serializable value for ${stateName}`)
      const safe = ensureSerializable(value, stateName)
      setState(safe)
      return
    }

    // Double check with structuredClone
    if (value !== null && value !== undefined && !isCloneable(value)) {
      console.error(`[SafeSetState] Value not cloneable for ${stateName}`)
      setState(null)
      return
    }

    setState(value)
  } catch (error) {
    console.error(`[SafeSetState] Error setting ${stateName}:`, error.message)
    setState(null)
  }
}
